import React, { useEffect, useRef } from 'react';
import { useSignal } from '@preact/signals-react';
import './LoadingComponent.css';
import { Box, CircularProgress } from '@mui/material';

interface LoadingComponentProps {
  messages?: string[];
  duration?: number;
  loop?: boolean;
  showSpinner?: boolean;
  className?: string;
  onComplete?: () => void;
}

const DocumentLoader: React.FC<LoadingComponentProps> = ({
  messages = [
    "Loading your document...",
    "Your document is almost ready...",
    "This is taking longer than expected, but we're almost there..."
  ],
  duration = 3000,
  loop = true,
  showSpinner = true,
  className = '',
  onComplete
}) => {
  const currentMessageIndex = useSignal(0);
  const isVisible = useSignal(true);
  const isComplete = useSignal(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isComplete.value) return;

    const timer = setTimeout(() => {
      isVisible.value = false;

      setTimeout(() => {
        if (currentMessageIndex.value < messages.length - 1) {
          currentMessageIndex.value = currentMessageIndex.value + 1;
        } else if (loop) {
          currentMessageIndex.value = 0;
        } else {
          isComplete.value = true;
          onComplete?.();
          return;
        }
        isVisible.value = true;
      }, 300); // Half of transition duration for smooth fade
    }, duration);

    timerRef.current = timer;
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [currentMessageIndex.value, messages.length, duration, loop, isComplete.value, onComplete]);

  useEffect(() => {
    isVisible.value = true;
  }, [currentMessageIndex.value]);

  return (
    <div
      className={`loading-document-container ${className}`}
      role="status"
      aria-live="polite"
      aria-label="Loading content"
    >
      {showSpinner && (
        <Box
          sx={{ color: "var(--md-sys-color-primary)" }}
          className="document-loaded-spinner-container"
        >
          <CircularProgress size={48} aria-hidden="true" color="inherit" />
        </Box>
      )}

      <div className="message-container">
        <p
          className={`loading-message ${isVisible.value ? 'visible' : 'hidden'}`}
          aria-atomic="true"
        >
          {messages[currentMessageIndex.value]}
        </p>
      </div>
    </div>
  );
};

export default DocumentLoader;